import type {
    ComponentDescription,
    PluginConfig,
    PropertyDescription,
    TabDescription,
    ScreenDescription,
    AppDescription,
    BaseContainerComponentDescription,
    NonUserAppFeatures,
} from "@glide/app-description";
import { getScreenProperty, ScreenDescriptionKind } from "@glide/app-description";
import { removeTab, updateTabDescriptionForAppDescription } from "./app-action-utils";
import { getAppTabs, getSourceMetadata } from "@glide/common-core/dist/js/components/SerializedApp";
import { assertNever, defined, proveNever } from "@glideapps/ts-necessities";
import { handleReorderComponent } from "./app-action-reorder-components";
import { getScreenComponents, isClassOrArrayScreenDescription } from "@glide/common-core";
import { logError, replaceArrayItem } from "@glide/support";
import type { GoogleSheetSourceMetadata, SourceMetadata } from "@glide/type-schema";
import { areSourceMetadatasEqual } from "@glide/type-schema";
import { findComponentIndexByID, getSubComponents } from "@glide/generator/dist/js/sub-components";
import type { ComponentIndexes } from "@glide/common-core/dist/js/component-indexes";
import type { BaseTheme } from "../../../base-theme/dist/ts";

export enum AppMutationKind {
    UpdateTabDescription = "UPDATE_TAB_DESCRIPTION",
    AddSourceMetadata = "ADD_SOURCE_METADATA",
    AddOrReplaceGoogleSheetSourceMetadata = "ADD_OR_REPLACE_GOOGLE_SHEET_SOURCE_METADATA",
    RemoveTab = "REMOVE_TAB",
    AddTab = "ADD_TAB",
    UpdateSerializedApp = "UPDATE_SERIALIZED_APP",
    ReorderTabs = "REORDER_TABS",
    UpsertPluginConfig = "UPSERT_PLUGIN_CONFIG",
    DeletePluginConfig = "DELETE_PLUGIN_CONFIG",
    UpdatePluginConfigParameters = "UPDATE_PLUGIN_CONFIG_PARAMETERS",
    RemoveSourceMetadata = "REMOVE_NATIVE_TABLE_SOURCE_METADATA",
    UpdateComponentDescription = "UPDATE_COMPONENT_DESCRIPTION",
    AddTabTemplate = "ADD_TAB_TEMPLATE",
    UpdateScreenDescription = "UPDATE_SCREEN_DESCRIPTION",
    ReorderComponent = "REORDER_COMPONENT",
    ApplyUndo = "APPLY_UNDO",
    UpdateTheme = "UPDATE_THEME",
    UpdateFeatures = "UPDATE_FEATURES",
}

export interface UpdateTabDescriptionData {
    readonly screenName: string;
    readonly desc: TabDescription;
}

export interface AddSourceMetadataData {
    readonly sourceMetadata: SourceMetadata;
}

export interface AddOrReplaceGoogleSheetSourceMetadataData {
    readonly sourceMetadata: GoogleSheetSourceMetadata;
}

export interface RemoveTabData {
    readonly screenName: string;
}

export interface AddTabData {
    readonly tabDescription: TabDescription;
    readonly index: number;
}

export type UpdateSerializedAppData = Partial<AppDescription>;

export interface ReorderTabsData {
    readonly screenNames: readonly string[];
}

export interface UpsertPluginConfigData {
    readonly pluginConfig: PluginConfig;
}

export interface DeletePluginConfigData {
    readonly pluginConfig: PluginConfig;
}

export interface UpdatePluginConfigParametersData {
    readonly pluginConfigID: string;
    readonly pluginParams: Record<string, PropertyDescription>;
}

export interface ReorderComponentData {
    readonly screenName: string;
    readonly fromComponentID: string;
    readonly to: ComponentIndexes;
}

export interface RemoveSourceMetadataData {
    readonly sourceMetadata: SourceMetadata;
}

export interface UpdateComponentDescriptionData {
    readonly screenName: string;
    readonly componentID: string;
    readonly newDescription: ComponentDescription;
}

export interface AddTabTemplateData {
    readonly tabsToAdd: readonly TabDescription[];
    readonly screensToAdd: Record<string, ScreenDescription>;
}

export interface UpdateScreenDescriptionData {
    readonly screenName: string;
    /** `false` means delete the screen */
    readonly updates: Partial<ScreenDescription> | false;
    readonly allowNewScreen: boolean;
}

export interface ApplyUndoData {
    readonly appDescription: AppDescription;
}

export interface UpdateThemeData {
    readonly updates: Partial<BaseTheme>;
}

export interface UpdateFeaturesData {
    readonly updates: Partial<NonUserAppFeatures>;
}

interface MutType<TKind extends AppMutationKind, TData> {
    readonly kind: TKind;
    readonly data: TData;
}

type UpdateTabDescriptionMutation = MutType<AppMutationKind.UpdateTabDescription, UpdateTabDescriptionData>;
type AddSourceMetadataMutation = MutType<AppMutationKind.AddSourceMetadata, AddSourceMetadataData>;
type AddOrReplaceGoogleSheetSourceMetadataMutation = MutType<
    AppMutationKind.AddOrReplaceGoogleSheetSourceMetadata,
    AddOrReplaceGoogleSheetSourceMetadataData
>;
type RemoveTabMutation = MutType<AppMutationKind.RemoveTab, RemoveTabData>;
type AddTabMutation = MutType<AppMutationKind.AddTab, AddTabData>;
type UpdateSerializedAppMutation = MutType<AppMutationKind.UpdateSerializedApp, UpdateSerializedAppData>;
type ReorderTabsMutation = MutType<AppMutationKind.ReorderTabs, ReorderTabsData>;
type UpsertPluginConfigMutation = MutType<AppMutationKind.UpsertPluginConfig, UpsertPluginConfigData>;
type DeletePluginConfigMutation = MutType<AppMutationKind.DeletePluginConfig, DeletePluginConfigData>;
type UpdatePluginConfigParametersMutation = MutType<
    AppMutationKind.UpdatePluginConfigParameters,
    UpdatePluginConfigParametersData
>;
type ReorderComponentMutation = MutType<AppMutationKind.ReorderComponent, ReorderComponentData>;
type RemoveSourceMetadataMutation = MutType<AppMutationKind.RemoveSourceMetadata, RemoveSourceMetadataData>;
type UpdateComponentDescriptionMutation = MutType<
    AppMutationKind.UpdateComponentDescription,
    UpdateComponentDescriptionData
>;
type AddTabTemplateMutation = MutType<AppMutationKind.AddTabTemplate, AddTabTemplateData>;
type UpdateScreenDescriptionMutation = MutType<AppMutationKind.UpdateScreenDescription, UpdateScreenDescriptionData>;
type ApplyUndoMutation = MutType<AppMutationKind.ApplyUndo, ApplyUndoData>;
type UpdateThemeMutation = MutType<AppMutationKind.UpdateTheme, UpdateThemeData>;
type UpdateFeaturesMutation = MutType<AppMutationKind.UpdateFeatures, UpdateFeaturesData>;

export type AppDescriptionMutation =
    | UpdateTabDescriptionMutation
    | AddSourceMetadataMutation
    | AddOrReplaceGoogleSheetSourceMetadataMutation
    | RemoveTabMutation
    | AddTabMutation
    | UpdateSerializedAppMutation
    | ReorderTabsMutation
    | UpsertPluginConfigMutation
    | DeletePluginConfigMutation
    | UpdatePluginConfigParametersMutation
    | RemoveSourceMetadataMutation
    | UpdateComponentDescriptionMutation
    | AddTabTemplateMutation
    | UpdateScreenDescriptionMutation
    | ReorderComponentMutation
    | ApplyUndoMutation
    | UpdateThemeMutation
    | UpdateFeaturesMutation;

function updateScreenDescription(
    appDesc: AppDescription,
    screenName: string,
    cb: (oldScreenDesc: ScreenDescription | undefined) => ScreenDescription | undefined
): AppDescription {
    const screenDesc = appDesc.screenDescriptions[screenName];

    const newScreenDescription = cb(screenDesc);
    let screenDescriptions: Record<string, ScreenDescription>;
    if (newScreenDescription !== undefined) {
        screenDescriptions = {
            ...appDesc.screenDescriptions,
            [screenName]: newScreenDescription,
        };
    } else {
        screenDescriptions = { ...appDesc.screenDescriptions };
        delete screenDescriptions[screenName];
    }

    return {
        ...appDesc,
        screenDescriptions,
    };
}

export function applyMutation(oldDesc: AppDescription, mutation: AppDescriptionMutation): AppDescription {
    switch (mutation.kind) {
        case AppMutationKind.UpdateTabDescription: {
            const { screenName, desc } = mutation.data;
            const index = getAppTabs(oldDesc).findIndex(t => getScreenProperty(t.screenName) === screenName);
            if (index < 0) return oldDesc;
            return updateTabDescriptionForAppDescription(oldDesc, index, desc);
        }
        case AppMutationKind.AddSourceMetadata: {
            const { sourceMetadata } = mutation.data;
            const serializedAppSourceMetadata = getSourceMetadata(oldDesc).reduce(
                (acc, sm) => (sourceMetadata.id === sm.id && sourceMetadata.type === sm.type ? [...acc] : [...acc, sm]),
                [sourceMetadata]
            );
            return {
                ...oldDesc,
                sourceMetadataArray: serializedAppSourceMetadata,
            };
        }
        case AppMutationKind.AddOrReplaceGoogleSheetSourceMetadata: {
            const { sourceMetadata } = mutation.data;
            return {
                ...oldDesc,
                sourceMetadataArray: [
                    ...getSourceMetadata(oldDesc).filter(sm => sm.type !== "Google Sheet"),
                    sourceMetadata,
                ],
                sourceMetadata: undefined,
            };
        }
        case AppMutationKind.RemoveTab: {
            return removeTab(oldDesc, mutation.data.screenName);
        }
        case AppMutationKind.ReorderComponent: {
            const screenDesc = oldDesc.screenDescriptions[mutation.data.screenName];
            if (screenDesc === undefined) {
                logError("Screen description not found");
                return oldDesc;
            }
            const fromIndexes = findComponentIndexByID(screenDesc, mutation.data.fromComponentID);
            if (fromIndexes === undefined) {
                logError("Component not found in screen description");
                return oldDesc;
            }
            return updateScreenDescription(
                oldDesc,
                mutation.data.screenName,
                old => handleReorderComponent(old, fromIndexes, mutation.data.to) ?? old
            );
        }
        case AppMutationKind.AddTab: {
            const { tabDescription, index } = mutation.data;

            const screenName = getScreenProperty(tabDescription.screenName);
            if (screenName === undefined) {
                logError("New tab has no screen name");
                return oldDesc;
            }

            const screen = oldDesc.screenDescriptions[screenName];
            if (screen === undefined) {
                logError("Screen for new tab not found");
                return oldDesc;
            }

            if (screen.kind === ScreenDescriptionKind.Class || screen.kind === ScreenDescriptionKind.Array) {
                if (screen.fetchesData !== true) {
                    logError("Screen must fetch its own data for new tab");
                    return oldDesc;
                }
            } else {
                if (screen.kind !== ScreenDescriptionKind.Chat && screen.kind !== ScreenDescriptionKind.ShoppingCart) {
                    return proveNever(screen, `Unknown screen kind ${(screen as any).kind}`, oldDesc);
                }
            }

            const oldTabs = getAppTabs(oldDesc);
            const newTabs = [...oldTabs.slice(0, index), tabDescription, ...oldTabs.slice(index)];

            return { ...oldDesc, tabs: newTabs };
        }
        case AppMutationKind.UpdateSerializedApp: {
            const updates = mutation.data;
            return {
                ...oldDesc,
                ...updates,
            };
        }
        case AppMutationKind.UpsertPluginConfig: {
            // This is copied from `upsertPluginConfig` in `actions.ts`.  We
            // can remove it there when we get rid of the
            // `useSmallReduxActions` feature setting.
            const { pluginConfig: newConfig } = mutation.data;

            let pluginConfigs = oldDesc.pluginConfigs ?? [];

            let pluginConfig: PluginConfig | undefined;
            if (newConfig.configID !== undefined) {
                // Find the existing config by config ID.
                pluginConfig = pluginConfigs.find(p => p.configID === newConfig.configID);
            }
            if (pluginConfig === undefined) {
                // If we don't have a config ID match, we find an existing config
                // with the same plugin ID that doesn't have a config ID.
                pluginConfig = pluginConfigs.find(p => p.configID === undefined && p.pluginID === newConfig.pluginID);
            }
            if (pluginConfig !== undefined) {
                // If there is an existing config, we remove it from the list
                // before adding the new config.
                pluginConfigs = pluginConfigs.filter(p => p !== pluginConfig);
            }

            return {
                ...oldDesc,
                pluginConfigs: [...pluginConfigs, newConfig],
            };
        }
        case AppMutationKind.DeletePluginConfig: {
            // This is copied from `deletePluginConfig` in `actions.ts`.  We
            // can remove it there when we get rid of the
            // `useSmallReduxActions` feature setting.
            const { pluginConfig } = mutation.data;
            const pluginConfigs = oldDesc.pluginConfigs ?? [];
            const newConfigs = [
                ...pluginConfigs.filter(p => {
                    if (pluginConfig.configID !== undefined) {
                        return p.configID !== pluginConfig.configID;
                    } else if (p.configID === undefined) {
                        // If we don't have a config ID then we have to match
                        // by plugin ID if `p` also doesn't have a config ID.
                        return p.pluginID !== pluginConfig.pluginID;
                    } else {
                        return true;
                    }
                }),
            ];
            return {
                ...oldDesc,
                pluginConfigs: newConfigs,
            };
        }
        case AppMutationKind.UpdatePluginConfigParameters: {
            const { pluginConfigID, pluginParams } = mutation.data;
            const pluginConfigs = oldDesc.pluginConfigs;
            if (pluginConfigs === undefined) return oldDesc;

            const updatedPluginConfigs = pluginConfigs.map(pc => {
                if (pc.configID !== pluginConfigID) return pc;

                const updatedParameters = { ...pc.parameters, ...pluginParams };
                return { ...pc, parameters: updatedParameters };
            });
            return {
                ...oldDesc,
                pluginConfigs: updatedPluginConfigs,
            };
        }
        case AppMutationKind.ReorderTabs: {
            const { screenNames } = mutation.data;

            // We will remove tabs here as we go
            let oldTabs = getAppTabs(oldDesc);
            const newTabs: TabDescription[] = [];

            for (const screenName of screenNames) {
                const tab = oldTabs.find(t => getScreenProperty(t.screenName) === screenName);
                if (tab === undefined) continue;

                newTabs.push(tab);
                oldTabs = oldTabs.filter(t => t !== tab);
            }

            // Add remaining tabs, if any
            newTabs.push(...oldTabs);

            return { ...oldDesc, tabs: newTabs };
        }
        case AppMutationKind.RemoveSourceMetadata: {
            const { sourceMetadata } = mutation.data;
            return {
                ...oldDesc,
                sourceMetadataArray: getSourceMetadata(oldDesc).filter(
                    sm => !areSourceMetadatasEqual(sm, sourceMetadata)
                ),
            };
        }
        case AppMutationKind.AddTabTemplate: {
            const { screensToAdd, tabsToAdd } = mutation.data;

            const existingTabs = getAppTabs(oldDesc);

            const tabs = [...existingTabs, ...tabsToAdd];

            const screenDescriptions = { ...oldDesc.screenDescriptions };
            for (const [screenName, screen] of Object.entries(screensToAdd)) {
                screenDescriptions[screenName] = screen;
            }

            return { ...oldDesc, tabs, screenDescriptions };
        }
        case AppMutationKind.UpdateScreenDescription: {
            const { screenName, updates, allowNewScreen } = mutation.data;
            return updateScreenDescription(oldDesc, screenName, old => {
                if (updates === false) return undefined;
                if (old === undefined) {
                    if (allowNewScreen) {
                        return updates as ScreenDescription;
                    }
                    return undefined;
                }

                return { ...old, ...updates } as ScreenDescription;
            });
        }
        case AppMutationKind.UpdateComponentDescription: {
            const { screenName, componentID, newDescription } = mutation.data;
            return updateScreenDescription(oldDesc, screenName, old => {
                if (!isClassOrArrayScreenDescription(old)) return old;
                const components = getScreenComponents(old);
                const componentIndexes = findComponentIndexByID(old, componentID);
                if (componentIndexes === undefined) {
                    logError("Component not found in screen description");
                    return old;
                }

                let newScreenDescription: ScreenDescription;
                if (componentIndexes.length === 1) {
                    const newComponents = replaceArrayItem(components, componentIndexes[0], newDescription);
                    newScreenDescription = { ...old, components: newComponents };
                } else if (componentIndexes.length === 2) {
                    const parent = defined(components[componentIndexes[0]]) as BaseContainerComponentDescription;
                    const subComponents = defined(getSubComponents(parent));
                    const newSubComponents = replaceArrayItem(subComponents, componentIndexes[1], newDescription);
                    const newParent = { ...parent, components: newSubComponents };
                    const newComponents = replaceArrayItem(components, componentIndexes[0], newParent);
                    newScreenDescription = { ...old, components: newComponents };
                } else {
                    return assertNever(componentIndexes);
                }

                return newScreenDescription;
            });
        }
        case AppMutationKind.ApplyUndo: {
            const { appDescription } = mutation.data;
            return appDescription;
        }
        case AppMutationKind.UpdateTheme: {
            const { updates: theme } = mutation.data;
            return { ...oldDesc, theme: { ...oldDesc.theme, ...theme } };
        }
        case AppMutationKind.UpdateFeatures: {
            const { updates } = mutation.data;
            return { ...oldDesc, features: { ...oldDesc.features, ...updates } };
        }
        default: {
            assertNever(mutation);
        }
    }
}
